import { StyleSheet, View } from "react-native";
import { useAppConfig } from "../../../AppConfigProvider";
import {
  useDeleteAccountMutation,
  useExportCsvQuery,
  useGetCoursesForTranscriptGenerationQuery,
  useResetAccountMutation,
  MeDocument,
  useUpdateNotificationsSettingMutation,
} from "../../../graphql/generated/graphql";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { usePostHog } from "posthog-js/react";
import { useState, useEffect } from "react";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useVisitSession } from "../../../contexts/VisitSessionContext";
import { useAuth } from "../../../hooks/useAuth";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { exportConversationTranscript } from "../ProfileTabContainer/exportConversationTranscript";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { colors, spacing, radii, typographyScale } from "../../../styles/design-system";
import AxioButton from "../../../components/axio-ds/Button/AxioButton";
import DeleteAccountConfirmationModal from "../../../components/settings/DeleteAccountConfirmationModal/DeleteAccountConfirmationModal";
import ExportAccountConfirmationModal from "../../../components/settings/ExportAccountConfirmationModal/ExportAccountConfirmationModal";
import ResetAccountConfirmationModal from "../../../components/settings/ResetAccountConfirmationModal/ResetAccountConfirmationModal";
import DeleteAccountModalContainer from "../ProfileTabContainer/DeleteAccountModalContainer/DeleteAccountModalContainer";
import ExportAccountModalContainer from "../ProfileTabContainer/ExportAccountModalContainer/ExportAccountModalContainer";
import ResetAccountModalContainer from "../ProfileTabContainer/ResetAccountModalContainer/ResetAccountModalContainer";
import TwoFactorAuthDisableContainer from "../ProfileTabContainer/TwoFactorAuthModalContainer/TwoFactorAuthDisableContainer";
import TwoFactorAuthModalContainer from "../ProfileTabContainer/TwoFactorAuthModalContainer/TwoFactorAuthModalContainer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomSwitch from "../../../components/common/general/CustomSwitch/CustomSwitch";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { FileDown, Download, User, Database, Bell, AlertTriangle, RotateCcw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

enum ModalState {
  NONE = "NONE",
  DELETE = "DELETE",
  RESET = "RESET",
  EXPORT = "EXPORT",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  RESET_CONFIRMATION = "RESET_CONFIRMATION",
  EXPORT_CONFIRMATION = "EXPORT_CONFIRMATION",
  TWO_FACTOR_AUTH = "TWO_FACTOR_AUTH",
  TWO_FACTOR_DISABLE = "TWO_FACTOR_DISABLE",
}

export const UserData = () => {
  const { t } = useTranslation();
  const { dispatch } = useAppState();
  const posthog = usePostHog();
  const appConfig = useAppConfig();
  const { showToast } = useToast();
  const { isLarge } = useResponsiveScreen();
  const { logout, meData, refetchMe } = useAuth();
  const { endSession } = useVisitSession();
  const [modalState, setModalState] = useState(ModalState.NONE);
  const [exportButtonHovered, setExportButtonHovered] = useState(false);
  const [downloadButtonHovered, setDownloadButtonHovered] = useState(false);
  const [deleteAccount] = useDeleteAccountMutation();
  const [resetAccount] = useResetAccountMutation();
  const { refetch: exportCsvRefetch } = useExportCsvQuery({
    skip: true,
  });
  const [updateNotificationsSetting] = useUpdateNotificationsSettingMutation({
    refetchQueries: [
      {
        query: MeDocument,
      },
    ],
  });

  const schoolId = appConfig.axioSchools[0];

  const { data: exportData } = useGetCoursesForTranscriptGenerationQuery({
    variables: {
      schoolId,
    },
    skip: !schoolId,
  });

  const handleLogout = async () => {
    await endSession();
    await logout();
  };

  const handleDeleteAccount = async (typedText: string) => {
    if (typedText === "DELETE") {
      try {
        await AsyncStorage.setItem("deletionInProgress", "true");

        const { data: deleteAccountData } = await deleteAccount();
        if (deleteAccountData?.deleteAccount?.success) {
          setModalState(ModalState.DELETE_CONFIRMATION);
        } else {
          showToast({
            message: deleteAccountData?.deleteAccount?.message || "Failed to delete account.",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      }
    } else {
      showToast({
        message: "You must type DELETE to confirm.",
        type: "warning",
      });
    }
  };

  const handleResetAccount = () => {
    resetAccount()
      .then(({ data: resetAccountData }) => {
        if (resetAccountData?.resetAccount?.success) {
          setModalState(ModalState.RESET_CONFIRMATION);
        } else {
          showToast({
            message: resetAccountData?.resetAccount?.message || "Failed to reset account.",
            type: "error",
          });
        }
      })
      .catch(() => {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      });
  };

  const handleExportAccount = () => {
    exportCsvRefetch()
      .then(({ data: exportCsvData }) => {
        if (exportCsvData?.exportCsv?.success) {
          const blob = new Blob([exportCsvData.exportCsv.csvData], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.setAttribute("href", url);
          link.setAttribute("download", "user_data.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setModalState(ModalState.EXPORT_CONFIRMATION);
        } else {
          showToast({
            message: exportCsvData?.exportCsv?.message || "Failed to export account data.",
            type: "error",
          });
        }
      })
      .catch(() => {
        showToast({
          message: "An error occurred. Please try again.",
          type: "error",
        });
      });
  };

  const handleModalOpen = (modalState: ModalState) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: false },
    });
    setModalState(modalState);
  };

  const handleModalClose = (callback?: () => void) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setModalState(ModalState.NONE);
    if (callback) {
      callback();
    }
  };

  const handleResetModalClose = (callback?: () => void) => {
    dispatch({
      type: actions.SET_META,
      payload: { keypressIsListening: true },
    });
    setModalState(ModalState.NONE);
    window.location.reload(); // Refresh the site after reset
    if (callback) {
      callback();
    }
  };

  const handleDeleteAccountConfirmationCloseCb = async () => {
    await AsyncStorage.removeItem("deletionInProgress");
    await handleLogout();
  };

  useEffect(() => {
    const checkDeletionState = async () => {
      const deletionInProgress = await AsyncStorage.getItem("deletionInProgress");
      if (deletionInProgress === "true") {
        setModalState(ModalState.DELETE_CONFIRMATION);
      }
    };

    checkDeletionState();
    posthog?.capture("Settings Page Visited");
  }, []);

  const handleDownloadTranscript = async () => {
    await exportConversationTranscript(exportData?.getCoursesForTranscriptGeneration.data);
  };

  const handleDailyEmailOptInPress = async () => {
    try {
      await updateNotificationsSetting({
        variables: {
          dailyEmailOptIn: !meData?.me?.dailyEmailOptIn,
        },
      });
    } catch (error) {
      console.error("Failed to update daily email setting", error);
      showToast({
        message: "Failed to update notification setting",
        type: "error",
      });
    }
  };

  return (
    <View style={styles.outerContainer}>
      {/* Profile Section */}
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <User size={18} color="#6B7280" />
          <CustomText
            style={styles.sectionHeaderTitle}
            text="Profile"
            accessibilityLabel="Profile"
            accessibilityRole="text"
            aria-level="2"
            role="heading"
          />
        </View>
        <View>
          <CustomText text="Name" style={styles.fieldLabel} accessibilityLabel="Name" accessibilityRole="text" />
          <View style={styles.inputWrapper}>
            <CustomTextInput
              inputType={undefined}
              value={meData?.me?.name}
              onChangeText={undefined}
              placeholder={undefined}
              style={{ pointerEvents: "none", maxWidth: isLarge ? 448 : "100%" }}
            />
          </View>
        </View>
      </View>

      {/* Data & Privacy Section */}
      {/* <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Database size={18} color="#6B7280" />
          <CustomText
            style={styles.sectionHeaderTitle}
            text="Data & Privacy"
            accessibilityLabel="Data & Privacy"
            accessibilityRole="text"
            aria-level="2"
            role="heading"
          />
        </View>
        <View style={styles.dataPrivacyContent}>
          <View style={[styles.dataPrivacyItem, !isLarge && styles.dataPrivacyItemMobile]}>
            <View style={styles.dataPrivacyItemContent}>
              <CustomText text="Export Account Data" style={styles.dataPrivacyTitle} />
              <CustomText
                text="Download a copy of all your account information and activity"
                style={styles.dataPrivacySubtitle}
              />
            </View>
            <CustomButton
              text="Export"
              styleType="lightStroke"
              style={[styles.dataButton, exportButtonHovered && styles.dataButtonHovered]}
              textStyle={styles.dataButtonText}
              onPress={() => handleModalOpen(ModalState.EXPORT)}
              onHover={setExportButtonHovered}
              size="s"
              leftIcon={<Download size={16} color={colors.grey[700]} />}
            />
          </View>
          <View style={[styles.dataPrivacyItem, { borderTopWidth: 0 }, !isLarge && styles.dataPrivacyItemMobile]}>
            <View style={styles.dataPrivacyItemContent}>
              <CustomText text="Course Chat Transcripts" style={styles.dataPrivacyTitle} />
              <CustomText
                text="Download a complete history of your AI Companion conversations"
                style={styles.dataPrivacySubtitle}
              />
            </View>
            <CustomButton
              text="Download"
              styleType="lightStroke"
              style={[styles.dataButton, downloadButtonHovered && styles.dataButtonHovered]}
              textStyle={styles.dataButtonText}
              onPress={handleDownloadTranscript}
              onHover={setDownloadButtonHovered}
              size="s"
              leftIcon={<FileDown size={16} color={colors.grey[700]} />}
            />
          </View>
        </View>
      </View> */}
      {/* Danger Zone Section */}
      <View style={styles.dangerZoneContainer}>
        <View style={styles.sectionHeader}>
          <AlertTriangle size={18} color={colors.error[600]} />
          <CustomText
            style={styles.dangerZoneHeaderTitle}
            text="Danger Zone"
            accessibilityLabel="Danger Zone"
            accessibilityRole="text"
            aria-level="2"
            role="heading"
          />
        </View>
        <CustomText text="These actions are permanent and cannot be undone." style={styles.dangerZoneSubtitle} />
        <View style={styles.dangerZoneContent}>
          {/* Reset Account */}
          <View style={[styles.dangerZoneItem, !isLarge && styles.dangerZoneItemMobile]}>
            <View style={styles.dangerZoneItemContent}>
              <CustomText text="Reset Account" style={styles.dangerZoneItemTitle} />
              <CustomText
                text="Clear all your conversations and start fresh. Your profile will be preserved."
                style={styles.dangerZoneItemSubtitle}
              />
            </View>
            <AxioButton
              label={t("buttons.Reset")}
              variant="ghost"
              size="md"
              onPress={() => handleModalOpen(ModalState.RESET)}
              leftIcon={<RotateCcw size={16} color={colors.grey[700]} style={{ marginRight: spacing.xs }} />}
              style={{ maxWidth: 95 }}
              textStyle={{ fontSize: typographyScale.textXs.fontSize }}
            />
          </View>

          {/* Delete Account */}
          <View style={[styles.dangerZoneItem, styles.dangerZoneItemLast, !isLarge && styles.dangerZoneItemMobile]}>
            <View style={styles.dangerZoneItemContent}>
              <CustomText text="Delete Account" style={styles.dangerZoneItemTitle} />
              <CustomText
                text="Permanently delete your account and all associated data"
                style={styles.dangerZoneItemSubtitle}
              />
            </View>
            <AxioButton
              label={t("buttons.Delete")}
              variant="destructive"
              size="md"
              onPress={() => handleModalOpen(ModalState.DELETE)}
              leftIcon={<Trash2 size={16} color={colors.white} style={{ marginRight: spacing.xs }} />}
              style={{ maxWidth: 95 }}
              textStyle={{ fontSize: typographyScale.textXs.fontSize }}
            />
          </View>
        </View>
      </View>

      {/* Preferences Section */}
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Bell size={18} color="#6B7280" />
          <CustomText
            style={styles.sectionHeaderTitle}
            text="Preferences"
            accessibilityLabel="Preferences"
            accessibilityRole="text"
            aria-level="2"
            role="heading"
          />
        </View>
        <View style={[styles.preferencesItem, !isLarge && styles.preferencesItemMobile]}>
          <View style={styles.preferencesItemContent}>
            <CustomText text="Email Notifications" style={styles.preferencesTitle} />
            <CustomText
              text="Receive a daily summary of your upcoming tasks and assignments"
              style={styles.preferencesSubtitle}
            />
          </View>
          <View style={styles.switchWrapper}>
            <CustomSwitch value={meData?.me?.dailyEmailOptIn} onValueChange={handleDailyEmailOptInPress} />
          </View>
        </View>
      </View>

      {/* Modals */}
      <DeleteAccountModalContainer
        visible={modalState === ModalState.DELETE}
        onClose={handleModalClose}
        onDelete={handleDeleteAccount}
      />
      <ResetAccountModalContainer
        visible={modalState === ModalState.RESET}
        onClose={handleModalClose}
        onReset={handleResetAccount}
      />
      <ExportAccountModalContainer
        visible={modalState === ModalState.EXPORT}
        onClose={handleModalClose}
        onExport={handleExportAccount}
      />
      <DeleteAccountConfirmationModal
        visible={modalState === ModalState.DELETE_CONFIRMATION}
        onClose={() => handleModalClose(handleDeleteAccountConfirmationCloseCb)}
      />
      <ResetAccountConfirmationModal
        visible={modalState === ModalState.RESET_CONFIRMATION}
        onClose={handleResetModalClose}
      />
      <ExportAccountConfirmationModal
        visible={modalState === ModalState.EXPORT_CONFIRMATION}
        onClose={handleModalClose}
      />
      <TwoFactorAuthModalContainer visible={modalState === ModalState.TWO_FACTOR_AUTH} onClose={handleModalClose} />
      <TwoFactorAuthDisableContainer
        visible={modalState === ModalState.TWO_FACTOR_DISABLE}
        onClose={handleModalClose}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  sectionContainer: {
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
  },
  sectionHeaderTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#111827",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
    marginBottom: 8,
  },
  inputWrapper: {
    width: "100%",
  },
  dataPrivacyContent: {
    gap: 16,
  },
  dataPrivacyItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  dataPrivacyItemMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  dataPrivacyItemContent: {
    flex: 1,
    gap: 4,
  },
  dataPrivacyTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
  },
  dataPrivacySubtitle: {
    fontSize: 14,
    color: "#4B5563",
    marginTop: 4,
  },
  dataButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    // @ts-expect-error - web-only style
    transitionProperty: "all",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease-out",
  },
  dataButtonHovered: {
    transform: "scale(1.015)",
    backgroundColor: colors.grey[50],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  dataButtonText: {
    fontSize: 12,
    color: colors.grey[700],
    fontWeight: "500",
  },
  dangerZoneContainer: {
    backgroundColor: colors.error[50],
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  dangerZoneHeaderTitle: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "600",
    color: colors.error[700],
  },
  dangerZoneSubtitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[500],
    marginBottom: spacing.md,
  },
  dangerZoneContent: {
    gap: spacing.sm,
  },
  dangerZoneItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  dangerZoneItemLast: {
    borderBottomWidth: 0,
    paddingBottom: 0,
  },
  dangerZoneItemMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: spacing.xs,
  },
  dangerZoneItemContent: {
    flex: 1,
    gap: spacing.xxs,
    padding: spacing.none,
  },
  dangerZoneItemTitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: colors.grey[900],
  },
  dangerZoneItemSubtitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[500],
    marginTop: spacing.xxs,
  },
  preferencesItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  preferencesItemMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
  },
  preferencesItemContent: {
    flex: 1,
    gap: 4,
  },
  preferencesTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#111827",
  },
  preferencesSubtitle: {
    fontSize: 14,
    color: "#4B5563",
    marginTop: 4,
  },
  switchWrapper: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
});
